/**
 * Popover di selezione etichetta: stesso pattern DOM di SearchSelect
 * (contenitore relative + pannello assoluto, chiusura su mousedown
 * esterno), con ricerca inline OBBLIGATORIA (regola del progetto: nessun
 * dropdown senza filtro live).
 *
 * Il trigger e' un bottone con icona (default Tag) che apre il pannello;
 * ogni riga = pallino colorato + nome; click = selezione (onSeleziona).
 * Usato sia nella toolbar del viewer (applica al messaggio aperto) sia
 * nella barra bulk della lista (applica/rimuovi in batch, con icone
 * distinte via prop `icon`).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Tag } from 'lucide-react';
import { Button, Input, Tooltip } from '@floraltech/ui';
import type { EmailEtichetta } from '../lib/api';

interface Props {
  /** Etichette proposte nel pannello (tipicamente solo le attive). */
  etichette: EmailEtichetta[];
  onSeleziona: (etichetta: EmailEtichetta) => void;
  /** Testo del tooltip/aria-label del bottone trigger. */
  tooltip?: string;
  /** Icona del bottone trigger (default: Tag). */
  icon?: React.ReactNode;
  disabled?: boolean;
}

export function EtichettePicker({
  etichette,
  onSeleziona,
  tooltip = 'Etichetta',
  icon,
  disabled = false,
}: Props) {
  const [aperto, setAperto] = useState(false);
  const [filtro, setFiltro] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const chiudi = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setAperto(false);
        setFiltro('');
      }
    };
    document.addEventListener('mousedown', chiudi);
    return () => document.removeEventListener('mousedown', chiudi);
  }, []);

  const visibili = etichette.filter((e) =>
    e.nome.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div className="relative" ref={boxRef}>
      <Tooltip label={tooltip}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={tooltip}
          disabled={disabled}
          onClick={() => setAperto((v) => !v)}
        >
          {icon ?? <Tag className="h-4 w-4" />}
        </Button>
      </Tooltip>
      {aperto && (
        <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border bg-background shadow">
          <div className="p-1.5">
            <Input
              autoFocus
              value={filtro}
              placeholder="Cerca etichetta..."
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiltro(e.target.value)}
              autoComplete="off"
            />
          </div>
          <ul className="max-h-48 overflow-auto">
            {visibili.length === 0 && (
              <li className="px-3 py-2 text-xs text-muted-foreground">Nessuna etichetta</li>
            )}
            {visibili.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    onSeleziona(e);
                    setAperto(false);
                    setFiltro('');
                  }}
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: e.colore }}
                    aria-hidden="true"
                  />
                  <span className="truncate">{e.nome}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
